import * as _ from 'lodash'
import {BaselineBO} from "./BaselineBO"
import {MessageType} from "../PA.Core/PA.Core.IF.Basic"
import {ErrorMessage_restate, InfoMessage_restate} from "../PA.Core/Basic/MessageUtils"
import {WB_FinishedProductRow, WB_PartitionRRow, WB_PeriodRow, WB_ProductGroupRow} from '../PA.Core/PAdbSchemaRows'
import {CustomerHierarchyService} from '../PA.MD.Services/CustomerHierarchyService'
import {PeriodService} from '../PA.MD.Services/PeriodService'
import {ProductService} from '../PA.MD.Services/ProductService'
import {ProductHierarchyService} from '../PA.MD.Services/ProductHierarchyService'
import {Product_Hierarchy_Common_Levels} from '../PA.MD.Core/MD.Constants'

export class BaselineBOValidations {

    static async BeforeSave(aBO: BaselineBO): Promise<void> {

    }

    static async Validate(aBO: BaselineBO): Promise<void> {

        await BaselineBOValidations.BeforeSave(aBO)

        const emptyId: boolean = (_.isEmpty(aBO.WB_Baseline.cif.BASELINE_ID) || _.isNull(aBO.WB_Baseline.cif.BASELINE_ID))
        ErrorMessage_restate(aBO.ValidateMessageProvider, emptyId, "emptyId", "Baseline ID cannot be empty")

        const emptyName: boolean = (_.isEmpty(aBO.WB_Baseline.cif.BASELINE_NAME) || _.isNull(aBO.WB_Baseline.cif.BASELINE_NAME))
        ErrorMessage_restate(aBO.ValidateMessageProvider, emptyName, "emptyName", "Baseline Name cannot be empty")

        const emptySource: boolean = (_.isEmpty(aBO.WB_Baseline.cif.BASELINE_SOURCE_ID) || _.isNull(aBO.WB_Baseline.cif.BASELINE_SOURCE_ID))
        ErrorMessage_restate(aBO.ValidateMessageProvider, emptySource, "emptySource", "Source cannot be empty")

        const checkSourcePA: boolean = (aBO.WB_Baseline.cif.BASELINE_SOURCE_ID === 'PA' || aBO.WB_Baseline.cif.BASELINE_SOURCE_ID === 'Performance Analyzer')
        const emptyBaselineType: boolean = (!emptySource && checkSourcePA && (_.isEmpty(aBO.WB_Baseline.cif.BASELINE_TYPE_ID) || _.isNull(aBO.WB_Baseline.cif.BASELINE_TYPE_ID)))
        ErrorMessage_restate(aBO.ValidateMessageProvider, emptyBaselineType, "emptyBaselineType", "Baseline Type cannot be empty")

        let currentDate = new Date()
        let selectedStartDate = aBO.WB_Baseline.WB_ScheduleRow.ScheduleStartDtTs
        const checkValidDate: boolean = (selectedStartDate < currentDate)
        InfoMessage_restate(aBO.ValidateMessageProvider, checkValidDate, "checkValidDate", "Schedule Start Date is in the past")

        if (aBO.partitionBO.WB_Partition?.WB_PartitionRRows?.length)
            await BaselineBOValidations.ValidateDistinctCustomerAndProductGroup(aBO)
    }

    static async ValidateDistinctCustomerAndProductGroup(aBO: BaselineBO): Promise<void> {
        const arrPartitionRRows: WB_PartitionRRow[] = aBO.partitionBO.WB_Partition.WB_PartitionRRows
        if (_.isEmpty(_.filter(arrPartitionRRows, r => r.isActive)))
            return

        const baselineCustHId = aBO.WB_Baseline.cif.WB_CUSTH_ID
        const baselineCustomerLevel = aBO.WB_Baseline.cif.CUSTOMER_LEVEL
        const baselinePrdHId = aBO.WB_Baseline.cif.WB_PRDH_ID
        const baselineProductLevel = aBO.WB_Baseline.cif.PRODUCT_LEVEL
        const baselineProductType = aBO.WB_Baseline.cif.PRODUCT_TYPE

        const aPeriodRow: WB_PeriodRow = await PeriodService.getPeriodByWBID(aBO.WB_Baseline.cif.WB_PERIOD_ID)
        const baselineStartDt = aPeriodRow.PeriodStartDt
        const baselineEndDt = aPeriodRow.PeriodEndDt

        const custIdListAtOutputLevel: number[] = await CustomerHierarchyService.getWbIDsByCustomerType(baselineCustHId, baselineCustomerLevel)

        let dctPrdGrpByCust: { [WB_CUST_ID: number]: { [WB_PRDGRP_ID: number]: WB_PartitionRRow } } = {}

        for (let aRow of arrPartitionRRows) {

            const custId = aRow.cif.WB_CUST_ID
            const prdGrpId = aRow.cif.WB_PRDGRP_ID

            if (custId in dctPrdGrpByCust) {
                ErrorMessage_restate(aBO.ValidateMessageProvider, true, "checkCustPrdGrpOverlap", "Save Failed. Please ensure that the same Product Group is not selected across “Ship To” customers within a “Planning Account” customer.")
            }

            let planningCustIds: number[]
            if (_.includes(custIdListAtOutputLevel, custId)) {
                planningCustIds = [custId]
            } else {
                const parent = await CustomerHierarchyService.getParent(baselineCustHId, custId, baselineStartDt, baselineEndDt, baselineCustomerLevel)
                if (!_.isUndefined(parent)) {
                    planningCustIds = [parent]
                } else {
                    planningCustIds = await CustomerHierarchyService.getChildren(baselineCustHId, custId, baselineStartDt, baselineEndDt, baselineCustomerLevel)
                }
            }

            let productIds: number[]
            if (baselineProductType === 'ProductGroup') {
                let prdGrpRow: WB_ProductGroupRow = await ProductService.getPrdGrpByWbID(prdGrpId)
                if (prdGrpRow.cif.LEVEL_ID === baselineProductLevel) {
                    productIds = await ProductHierarchyService.getChildren(baselinePrdHId, prdGrpId, baselineStartDt, baselineEndDt, Product_Hierarchy_Common_Levels.PrdLevel)
                } else {
                    const parentId = await ProductHierarchyService.getPrdGrpParent(baselinePrdHId, prdGrpId, baselineStartDt, baselineEndDt, baselineProductLevel)
                    if (!_.isUndefined(parentId)) {
                        productIds = await ProductHierarchyService.getChildren(baselinePrdHId, prdGrpId, baselineStartDt, baselineEndDt, Product_Hierarchy_Common_Levels.PrdLevel)
                    } else {
                        const outputIdList = await ProductHierarchyService.getChildren(baselinePrdHId, prdGrpId, baselineStartDt, baselineEndDt, baselineProductLevel)
                        for (let outputId of outputIdList) {
                            productIds = await ProductHierarchyService.getChildren(baselinePrdHId, outputId, baselineStartDt, baselineEndDt, Product_Hierarchy_Common_Levels.PrdLevel)
                        }
                    }
                }
            } else {
                productIds = await ProductHierarchyService.getChildren(baselinePrdHId, prdGrpId, baselineStartDt, baselineEndDt, Product_Hierarchy_Common_Levels.PrdLevel)
            }

            _.forEach(planningCustIds, (planningCustId: number) => {
                _.forEach(productIds, (productId: number) => {
                    if (planningCustId in dctPrdGrpByCust) {
                        if (productId in dctPrdGrpByCust[planningCustId]) {
                            return ErrorMessage_restate(
                                aBO.ValidateMessageProvider,
                                true,
                                "checkCustPrdGrpOverlap",
                                "Save Failed. Please ensure that the same Product Group is not selected across “Ship To” customers within a “Planning Account” customer."
                            )
                        } else {
                            dctPrdGrpByCust[planningCustId] = {
                                ...dctPrdGrpByCust[planningCustId],
                                [productId]: aRow
                            }
                        }
                    } else {
                        dctPrdGrpByCust = {
                            ...dctPrdGrpByCust,
                            [planningCustId]: {
                                [productId]: aRow
                            }
                        }
                    }
                })
            })
        }
        return
    }

}